// Configuración de pago contra entrega (COD) a nivel operador logístico.
// Escribe /billing/cod-config: el PUT crea una versión nueva y archiva la anterior.
// La config es inmutable y versionada porque la liquidación al comercio necesita
// reconstruir con qué reglas se cobró cada paquete (mismo criterio que la config de
// pago a repartidores). La moneda no se edita: la resuelve el backend desde el país
// de operación del operador, para que un cambio en el front no altere montos ya cobrados.
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AlertTriangle,
  Banknote,
  Camera,
  History,
  RefreshCw,
  Save,
} from "lucide-react";
import {
  getCodConfig,
  getCodConfigVersions,
  saveCodConfig,
} from "../../../../api/billing";
import { showToast } from "../../../../utils/showToast";
import { SectionCard } from "../../../../components/ui/SectionCard";
import { Switch } from "../../../../components/ui/Switch";
import { Button } from "../../../../components/ui/Button";
import { Skeleton } from "../../../../components/ui/Skeleton";
import { EmptyState } from "../../../../components/ui/EmptyState";

// Catálogo de medios de pago del contrato. El orden de este array es el orden
// canónico con que se arma `methods`: así el snapshot no marca "sucio" solo porque
// el backend devolvió los medios en otro orden.
const METHODS = [
  {
    value: "cash",
    label: "Efectivo",
    description:
      "El cliente paga en efectivo al repartidor. El dinero queda con el repartidor: se rinde al operador y el operador compensa al comercio.",
  },
  {
    value: "transfer_driver",
    label: "Transferencia al repartidor",
    description:
      "El cliente transfiere a la cuenta del repartidor. El dinero también queda con el repartidor: se rinde y se compensa al comercio.",
  },
  {
    value: "transfer_seller",
    label: "Transferencia al vendedor",
    description:
      "El cliente transfiere directo a la cuenta del comercio. El operador no recauda ese dinero: solo se guarda la evidencia del pago, no se rinde ni se compensa.",
  },
];

const METHOD_ORDER = METHODS.map((m) => m.value);

// Medios por transferencia: los únicos a los que aplica `skipDeliveryEvidenceOnTransfer`.
// En efectivo la app pide siempre la evidencia de una entrega normal.
const TRANSFER_METHODS = ["transfer_driver", "transfer_seller"];

const hasTransferMethod = (methods) =>
  Array.isArray(methods) && methods.some((m) => TRANSFER_METHODS.includes(m));

const DEFAULT_FORM = {
  enabled: false,
  methods: [],
  skipDeliveryEvidenceOnTransfer: false,
};

// El contrato del backend devuelve { data: … }. Toleramos también el documento
// pelado porque el resto de /billing responde sin envoltorio y el proxy del BFF
// no lo normaliza: preferimos leer ambas formas antes que romper por el envase.
const unwrap = (res) => {
  const body = res?.data;
  if (body && typeof body === "object" && !Array.isArray(body) && "data" in body) {
    return body.data;
  }
  return body ?? null;
};

const formFromConfig = (cfg) => {
  if (!cfg) return { ...DEFAULT_FORM };
  const saved = Array.isArray(cfg.methods) ? cfg.methods : [];
  return {
    enabled: !!cfg.enabled,
    methods: METHOD_ORDER.filter((v) => saved.includes(v)),
    // Estricto, igual que billing, el BFF y la app: una versión anterior al campo (o
    // con un valor que no es booleano) sigue pidiendo la evidencia como siempre.
    skipDeliveryEvidenceOnTransfer: cfg.skipDeliveryEvidenceOnTransfer === true,
  };
};

const methodLabel = (value) =>
  METHODS.find((m) => m.value === value)?.label ?? value;

const fmtDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleString("es-CL", { dateStyle: "short", timeStyle: "short" });
};

export default function CodSection() {
  const { t } = useTranslation();
  const [form, setForm] = useState(DEFAULT_FORM);
  const [snapshot, setSnapshot] = useState("");
  const [version, setVersion] = useState(null);
  const [currency, setCurrency] = useState(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [forbidden, setForbidden] = useState(false);
  const [loadError, setLoadError] = useState(false);
  // Bump para reintentar la carga desde el panel de error.
  const [reloadKey, setReloadKey] = useState(0);

  const [versions, setVersions] = useState(null);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setLoadError(false);
    setForbidden(false);
    getCodConfig()
      .then((res) => {
        if (!active) return;
        const cfg = unwrap(res);
        const next = formFromConfig(cfg);
        setForm(next);
        setSnapshot(JSON.stringify(next));
        setVersion(cfg?.version ?? null);
        setCurrency(cfg?.currency ?? null);
      })
      .catch((err) => {
        if (!active) return;
        if (err?.response?.status === 403) {
          setForbidden(true);
        } else {
          // NO renderizamos el formulario con defaults: con snapshot='' el Guardar
          // queda habilitado y el PUT crearía una versión nueva con la config
          // apagada, pisando la real del operador. Fallamos cerrado y ofrecemos
          // reintentar.
          setLoadError(true);
          showToast(
            t("No se pudo cargar la configuración de pago contra entrega"),
            false,
          );
        }
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [reloadKey]);

  const dirty = useMemo(
    () => JSON.stringify(form) !== snapshot,
    [form, snapshot],
  );

  const noMethods = form.enabled && form.methods.length === 0;

  // Sin COD o sin medios de transferencia el switch no tiene a qué aplicar: se bloquea
  // pero el valor guardado se conserva, para no perderlo al volver a habilitarlos.
  const transferEvidenceEditable = form.enabled && hasTransferMethod(form.methods);

  const toggleMethod = (value) =>
    setForm((f) => ({
      ...f,
      methods: f.methods.includes(value)
        ? f.methods.filter((m) => m !== value)
        : METHOD_ORDER.filter((m) => m === value || f.methods.includes(m)),
    }));

  const handleSave = async () => {
    if (noMethods) {
      showToast(
        t("Activa al menos un medio de pago para habilitar el pago contra entrega."),
        false,
      );
      return;
    }
    // Solo los campos del DTO: `currency` la resuelve el backend y
    // `logisticCompanyId` sale del JWT. `skipDeliveryEvidenceOnTransfer` viaja
    // SIEMPRE explícito: si faltara, billing lo heredaría de la versión activa y lo
    // que el operador cambió en el switch se perdería sin aviso.
    const payload = {
      enabled: form.enabled,
      methods: form.methods,
      skipDeliveryEvidenceOnTransfer: form.skipDeliveryEvidenceOnTransfer === true,
    };
    setSaving(true);
    try {
      const res = await saveCodConfig(payload);
      const cfg = unwrap(res);
      const next = cfg ? formFromConfig(cfg) : { ...form };
      setForm(next);
      setSnapshot(JSON.stringify(next));
      if (cfg?.version != null) setVersion(cfg.version);
      if (cfg?.currency) setCurrency(cfg.currency);
      // El historial en pantalla quedó viejo: se creó una versión nueva.
      setVersions(null);
      setHistoryOpen(false);
      showToast(t("Configuración guardada: se creó una versión nueva"), true);
    } catch (err) {
      if (err?.response?.status === 403) {
        showToast(
          t("Tu usuario no tiene permisos para configurar el pago contra entrega."),
          false,
        );
      } else {
        showToast(
          err?.response?.data?.message ?? t("No se pudo guardar la configuración"),
          false,
        );
      }
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (!snapshot) return;
    setForm(JSON.parse(snapshot));
  };

  const toggleHistory = async () => {
    if (historyOpen) {
      setHistoryOpen(false);
      return;
    }
    setHistoryOpen(true);
    if (versions) return;
    setVersionsLoading(true);
    try {
      const res = await getCodConfigVersions();
      const list = unwrap(res);
      setVersions(Array.isArray(list) ? list : []);
    } catch (err) {
      setHistoryOpen(false);
      showToast(
        err?.response?.data?.message ?? t("No se pudo cargar el historial de versiones"),
        false,
      );
    } finally {
      setVersionsLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col gap-5" role="status" aria-live="polite">
        <span className="sr-only">{t("Cargando…")}</span>
        <Skeleton className="h-4 w-full max-w-2xl" />
        {Array.from({ length: 3 }).map((_, i) => (
          <div
            key={i}
            className="rounded-lg border border-border bg-bg-muted p-5 space-y-4"
          >
            <div className="flex items-start gap-3">
              <Skeleton className="w-9 h-9 rounded-lg shrink-0" />
              <div className="flex-1 min-w-0 space-y-2">
                <Skeleton className="h-4 w-64 max-w-full" />
                <Skeleton className="h-3 w-96 max-w-full" />
              </div>
            </div>
            <Skeleton className="h-10 w-full max-w-md rounded-lg" />
          </div>
        ))}
      </div>
    );
  }

  if (forbidden) {
    return (
      <div className="rounded-lg border border-border bg-bg-muted p-4 text-sm text-fg-muted">
        {t("Tu usuario no pertenece a un operador logístico que pueda configurar el pago contra entrega.")}
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="rounded-lg border border-border bg-bg-muted" role="alert">
        <EmptyState
          icon={<AlertTriangle />}
          title={t("No se pudo cargar la configuración de pago contra entrega")}
          description={t("Para no crear una versión nueva encima de la configuración guardada, el formulario no se muestra hasta poder cargarla.")}
          action={
            <Button
              variant="outline"
              iconLeft={<RefreshCw size={16} aria-hidden />}
              onClick={() => setReloadKey((k) => k + 1)}
            >
              {t("Reintentar")}
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <p className="text-sm text-fg-muted">
        {t("El pago contra entrega permite que el repartidor cobre el pedido al momento de entregarlo. Si el cliente no paga, el repartidor no entrega: el pedido se marca como entrega fallida con el motivo «No pagó».")}
      </p>

      {/* Aviso de inmutabilidad: guardar NO edita la config vigente, crea otra. */}
      <div
        className="flex items-start gap-3 rounded-lg border border-border bg-bg-subtle p-4"
        role="note"
      >
        <History className="w-4 h-4 mt-0.5 shrink-0 text-fg-subtle" aria-hidden />
        <div className="min-w-0 text-sm text-fg-muted">
          <span className="font-medium text-fg">
            {t("Cada vez que guardas se crea una versión nueva.")}
          </span>{" "}
          {t("La configuración es inmutable: los cobros ya registrados quedan atados a la versión con la que se hicieron, para poder reconciliar las liquidaciones pasadas.")}
          {version != null && (
            <>
              {" "}
              {t("Versión vigente:")}{" "}
              <span className="font-medium text-fg">v{version}</span>.
            </>
          )}
          {version == null && (
            <> {t("Todavía no hay ninguna versión guardada para tu empresa.")}</>
          )}
        </div>
      </div>

      {/* Encendido/apagado */}
      <SectionCard
        icon={<Banknote className="w-4 h-4" />}
        title={t("Pago contra entrega")}
        subtitle={t("Actívalo para que tus comercios puedan marcar pedidos con monto a cobrar en la entrega.")}
        bodyClassName="flex flex-col gap-4"
      >
        <Switch
          id="cod-enabled"
          checked={form.enabled}
          onChange={(next) => setForm((f) => ({ ...f, enabled: next }))}
          disabled={saving}
          label={t("Habilitar el pago contra entrega")}
        />

        <div className="flex flex-wrap items-baseline gap-2 border-t border-border pt-4">
          <span className="text-xs font-medium text-fg-muted">{t("Moneda de cobro")}</span>
          <span className="inline-flex items-center h-7 px-2 rounded-md border border-border bg-bg text-sm font-medium text-fg">
            {currency || t("Sin definir")}
          </span>
          <span className="text-xs text-fg-muted">
            {t("Se resuelve desde el país de operación de tu empresa y no se edita acá.")}
          </span>
        </div>
      </SectionCard>

      {/* Medios de pago aceptados */}
      <SectionCard
        icon={<Banknote className="w-4 h-4" />}
        title={t("Medios de pago aceptados")}
        subtitle={t("Define con qué puede pagar el cliente al recibir el pedido. El repartidor solo verá los medios que habilites acá.")}
        bodyClassName="flex flex-col gap-3"
      >
        {METHODS.map((m) => {
          const checked = form.methods.includes(m.value);
          return (
            <div
              key={m.value}
              className={
                "flex items-start justify-between gap-4 rounded-lg border p-4 " +
                (checked ? "border-brand-500/40 bg-brand-50/40" : "border-border")
              }
            >
              <div className="min-w-0">
                <div className="font-medium text-fg text-sm">{t(m.label)}</div>
                <p className="text-sm text-fg-muted mt-0.5">{t(m.description)}</p>
              </div>
              <Switch
                checked={checked}
                onChange={() => toggleMethod(m.value)}
                disabled={saving || !form.enabled}
                ariaLabel={t("Aceptar {{label}}", { label: m.label })}
              />
            </div>
          );
        })}

        <div aria-live="polite">
          {noMethods && (
            <p className="text-xs text-status-error">
              {t("Habilita al menos un medio de pago o apaga el pago contra entrega.")}
            </p>
          )}
          {!form.enabled && (
            <p className="text-xs text-fg-muted">
              {t("Los medios de pago se editan con el pago contra entrega habilitado.")}
            </p>
          )}
        </div>
      </SectionCard>

      {/* Evidencia. El comprobante es informativo, NO configurable: «Transferencia ⇒
          comprobante obligatorio» es un invariante que el servidor valida siempre al
          registrar el cobro; si el operador pudiera apagarlo, la app dejaría de pedir la
          foto y el servidor rechazaría la entrega. Lo único que se elige es si ADEMÁS se
          pide la evidencia de una entrega normal (datos de quien recibe y fotos). */}
      <SectionCard
        icon={<Camera className="w-4 h-4" />}
        title={t("Evidencia del cobro")}
        subtitle={t("Qué debe adjuntar el repartidor al registrar el pago en la app.")}
        bodyClassName="flex flex-col gap-4"
      >
        <div
          className="rounded-lg border border-border bg-bg-subtle p-4 text-sm text-fg-muted"
          role="note"
        >
          <span className="font-medium text-fg">
            {t("En los dos medios de transferencia, el repartidor siempre debe adjuntar la foto del comprobante.")}
          </span>{" "}
          {t("Sin esa foto no puede registrar el cobro ni cerrar la entrega. El pago en efectivo no pide foto. El comprobante es una regla del sistema y no se configura por empresa.")}
        </div>

        <div className="border-t border-border pt-4">
          {/* El contrato guarda la OMISIÓN (`skip…`); la pantalla la muestra en positivo
              porque el operador piensa en qué se le pide al repartidor. La inversión
              vive solo en este render: el form y el payload llevan el valor del contrato. */}
          <Switch
            id="cod-transfer-delivery-evidence"
            checked={!form.skipDeliveryEvidenceOnTransfer}
            onChange={(next) =>
              setForm((f) => ({ ...f, skipDeliveryEvidenceOnTransfer: !next }))
            }
            disabled={saving || !transferEvidenceEditable}
            label={t("Pedir también la evidencia de una entrega normal")}
            aria-describedby="cod-transfer-delivery-evidence-help"
          />
          <div id="cod-transfer-delivery-evidence-help" className="mt-1.5 flex flex-col gap-1">
            <p className="text-xs text-fg-muted">
              {form.skipDeliveryEvidenceOnTransfer
                ? t("En los pagos por transferencia (al repartidor o al vendedor), el repartidor cierra la entrega solo con el comprobante, que sigue siendo obligatorio.")
                : t("Además del comprobante, el repartidor registra la misma evidencia que en una entrega normal, según lo configurado en Evidencia de entrega.")}
            </p>
            {!transferEvidenceEditable && (
              <p className="text-xs text-fg-muted">
                {t("Se edita con el pago contra entrega habilitado y al menos un medio de transferencia aceptado.")}
              </p>
            )}
          </div>
        </div>
      </SectionCard>

      {/* Acciones */}
      <div className="flex items-center justify-between gap-2 flex-wrap border-t border-border pt-4">
        <Button
          variant="ghost"
          size="sm"
          iconLeft={<History size={16} aria-hidden />}
          onClick={toggleHistory}
          disabled={versionsLoading}
        >
          {historyOpen ? t("Ocultar historial") : t("Ver historial de versiones")}
        </Button>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleReset} disabled={!dirty || saving}>
            {t("Descartar cambios")}
          </Button>
          <Button
            onClick={handleSave}
            loading={saving}
            disabled={!dirty || saving || noMethods}
            iconLeft={!saving ? <Save size={16} aria-hidden /> : undefined}
          >
            {saving ? t("Guardando…") : t("Guardar y crear versión")}
          </Button>
        </div>
      </div>

      {/* Historial de versiones */}
      {historyOpen && (
        <div className="rounded-lg border border-border bg-bg-muted p-4">
          {versionsLoading ? (
            <div className="space-y-2" role="status" aria-live="polite">
              <span className="sr-only">{t("Cargando historial…")}</span>
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
            </div>
          ) : (versions ?? []).length === 0 ? (
            <p className="text-sm text-fg-muted">{t("Todavía no hay versiones guardadas.")}</p>
          ) : (
            <div className="max-h-64 overflow-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-fg-subtle text-xs text-left">
                    <th className="py-1 pr-2">{t("Versión")}</th>
                    <th className="py-1 pr-2">{t("Estado")}</th>
                    <th className="py-1 pr-2">{t("Vigente desde")}</th>
                    <th className="py-1 pr-2">COD</th>
                    <th className="py-1 pr-2">{t("Medios")}</th>
                    <th className="py-1">{t("Evidencia en transferencias")}</th>
                  </tr>
                </thead>
                <tbody>
                  {versions.map((v) => (
                    <tr key={v._id ?? v.version} className="border-t border-border/60">
                      <td className="py-1 pr-2 text-fg">v{v.version}</td>
                      <td className="py-1 pr-2 text-fg-muted">
                        {v.status === "active" ? t("Vigente") : t("Archivada")}
                      </td>
                      <td className="py-1 pr-2 text-fg-muted">{fmtDate(v.validFrom)}</td>
                      <td className="py-1 pr-2 text-fg-muted">
                        {v.enabled ? t("Activo") : t("Apagado")}
                      </td>
                      <td className="py-1 pr-2 text-fg-muted">
                        {(Array.isArray(v.methods) ? v.methods : [])
                          .map((value) => t(methodLabel(value)))
                          .join(", ") || "—"}
                      </td>
                      <td className="py-1 text-fg-muted">
                        {/* Una versión con el cobro apagado o sin medios de transferencia no
                            aplicaba la regla: mostrar su valor haría creer que decidía algo. */}
                        {!v.enabled || !hasTransferMethod(v.methods)
                          ? "—"
                          : v.skipDeliveryEvidenceOnTransfer === true
                            ? t("Solo comprobante")
                            : t("Comprobante y evidencia")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
